<template>
  <section :class="['polls-panel', { 'polls-panel--compact': compact }]">
    <div class="polls-panel__header">
      <div>
        <p class="polls-panel__eyebrow">Video</p>
        <h3 class="polls-panel__title">{{ activePoll ? 'Encuesta del video' : 'Encuestas' }}</h3>
      </div>
      <v-chip small class="polls-panel__chip">
        {{ answeredPollsCount }}/{{ polls.length }}
      </v-chip>
    </div>

    <div v-if="activePoll" class="polls-panel__active">
      <div class="polls-panel__time">
        <v-icon size="18">mdi-clock-outline</v-icon>
        <span>{{ formatTime(activePoll.time_seconds) }}</span>
      </div>

      <h4>{{ activePoll.question }}</h4>

      <div v-if="activePoll.type !== 'open'" class="polls-panel__options">
        <button
          v-for="option in activePoll.options"
          :key="option.id"
          :class="[
            'polls-panel__option',
            {
              'polls-panel__option--selected': isSelectedOption(option.id),
              'polls-panel__option--correct': result && isCorrectOption(option.id),
              'polls-panel__option--incorrect': result && isSelectedOption(option.id) && !result.isCorrect,
            },
          ]"
          type="button"
          :disabled="Boolean(result)"
          @click="selectOption(option.id)"
        >
          <span>{{ option.text }}</span>
          <v-icon v-if="result && isCorrectOption(option.id)" size="18">
            mdi-check-circle-outline
          </v-icon>
          <v-icon v-else-if="result && isSelectedOption(option.id)" size="18">
            mdi-close-circle-outline
          </v-icon>
          <v-icon v-else-if="isSelectedOption(option.id)" size="18">
            mdi-check-circle
          </v-icon>
        </button>
      </div>

      <v-textarea
        v-else
        v-model="answerModel"
        outlined
        auto-grow
        rows="3"
        label="Escribe tu respuesta"
        :disabled="Boolean(result)"
        hide-details
      />

      <div
        v-if="result"
        :class="[
          'polls-panel__result',
          result.isCorrect === null
            ? 'polls-panel__result--neutral'
            : result.isCorrect
              ? 'polls-panel__result--correct'
              : 'polls-panel__result--incorrect',
        ]"
      >
        <v-icon size="22">
          {{ result.isCorrect === null
            ? 'mdi-message-text-outline'
            : result.isCorrect ? 'mdi-check-circle' : 'mdi-close-circle' }}
        </v-icon>
        <div>
          <strong>{{ result.isCorrect === null
            ? 'Respuesta registrada'
            : result.isCorrect ? 'Respuesta correcta' : 'Respuesta incorrecta' }}</strong>
          <span>Elegiste: {{ selectedOptionText }}</span>
        </div>
      </div>

      <div class="polls-panel__actions">
        <v-btn
          rounded
          depressed
          color="#0076ff"
          dark
          :disabled="!canSubmit && !result"
          @click="$emit(result ? 'continue' : 'submit')"
        >
          <v-icon left>{{ result ? 'mdi-arrow-right' : 'mdi-send-outline' }}</v-icon>
          {{ result ? 'Continuar' : 'Responder' }}
        </v-btn>
      </div>
    </div>

    <template v-else>
      <div v-if="polls.length === 0" class="polls-panel__empty">
        <v-icon color="#8a98a5" size="28">mdi-poll</v-icon>
        <span>Aun no hay encuestas para este video</span>
      </div>

      <div v-else class="polls-panel__list">
        <article
          v-for="poll in paginatedPolls"
          :key="poll.id"
          class="polls-panel__item"
        >
          <div class="polls-panel__item-main">
            <span class="polls-panel__timestamp">{{ formatTime(poll.time_seconds) }}</span>
            <p>{{ poll.question }}</p>
            <div
              v-if="getPollAnswer(poll.id)"
              :class="[
                'polls-panel__saved-answer',
                poll.type === 'open'
                  ? 'polls-panel__saved-answer--neutral'
                  : isPollAnswerCorrect(poll)
                    ? 'polls-panel__saved-answer--correct'
                    : 'polls-panel__saved-answer--incorrect',
              ]"
            >
              <v-icon size="18">
                {{ poll.type === 'open'
                  ? 'mdi-message-text-outline'
                  : isPollAnswerCorrect(poll) ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              <span>
                Tu respuesta: {{ getPollAnswerText(poll) }}
                <template v-if="poll.type !== 'open'">
                  ({{ isPollAnswerCorrect(poll) ? 'Correcta' : 'Incorrecta' }})
                </template>
              </span>
            </div>
          </div>
          <v-chip
            x-small
            :class="[
              'polls-panel__status',
              { 'polls-panel__status--answered': isAnswered(poll.id) },
            ]"
          >
            {{ isAnswered(poll.id) ? 'Respondida' : 'Pendiente' }}
          </v-chip>
        </article>
      </div>

      <div v-if="showPagination" class="polls-panel__pagination">
        <span class="polls-panel__pagination-meta">
          {{ paginationLabel }}
        </span>
        <v-pagination
          v-model="localCurrentPage"
          :length="totalPages"
          total-visible="5"
          color="#0076ff"
        />
      </div>
    </template>
  </section>
</template>

<script>
export default {
  name: 'ContentPolls',
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    polls: {
      type: Array,
      default: () => [],
    },
    activePoll: {
      type: Object,
      default: null,
    },
    answeredPollIds: {
      type: Array,
      default: () => [],
    },
    pollAnswers: {
      type: Object,
      default: () => ({}),
    },
    result: {
      type: Object,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localCurrentPage: 1,
      pollsPerPage: 4,
    };
  },
  computed: {
    answerModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    answeredPollsCount() {
      return this.polls.filter((poll) => this.isAnswered(poll.id)).length;
    },
    selectedOptionText() {
      if (!this.activePoll) return '';
      return this.formatAnswer(this.activePoll, this.answerModel);
    },
    canSubmit() {
      if (this.activePoll && this.activePoll.type === 'multiple') {
        return Array.isArray(this.answerModel) && this.answerModel.length > 0;
      }

      return this.answerModel !== null
        && this.answerModel !== undefined
        && String(this.answerModel).trim().length > 0;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.polls.length / this.pollsPerPage));
    },
    showPagination() {
      return this.totalPages > 1;
    },
    paginatedPolls() {
      const start = (this.localCurrentPage - 1) * this.pollsPerPage;
      return this.polls.slice(start, start + this.pollsPerPage);
    },
    paginationLabel() {
      if (this.polls.length === 0) return '0 encuestas';

      const start = (this.localCurrentPage - 1) * this.pollsPerPage + 1;
      const end = Math.min(this.localCurrentPage * this.pollsPerPage, this.polls.length);
      return `${start}-${end} de ${this.polls.length}`;
    },
  },
  watch: {
    polls() {
      if (this.localCurrentPage > this.totalPages) {
        this.localCurrentPage = this.totalPages;
      }
    },
  },
  methods: {
    isSelectedOption(optionId) {
      if (Array.isArray(this.answerModel)) {
        return this.answerModel.map(String).includes(String(optionId));
      }
      return String(optionId) === String(this.answerModel);
    },
    isCorrectOption(optionId) {
      if (!this.activePoll) return false;
      const correctIds = this.activePoll.type === 'multiple'
        ? this.activePoll.correct_option_ids || []
        : [this.activePoll.correct_option_id];
      return correctIds.map(String).includes(String(optionId));
    },
    selectOption(optionId) {
      if (this.activePoll.type !== 'multiple') {
        this.answerModel = optionId;
        return;
      }

      const selectedOptions = Array.isArray(this.answerModel) ? [...this.answerModel] : [];
      const optionIndex = selectedOptions.map(String).indexOf(String(optionId));
      if (optionIndex === -1) selectedOptions.push(optionId);
      else selectedOptions.splice(optionIndex, 1);
      this.answerModel = selectedOptions;
    },
    isAnswered(pollId) {
      return this.answeredPollIds.map(String).includes(String(pollId));
    },
    getPollAnswer(pollId) {
      return this.pollAnswers[String(pollId)];
    },
    getPollAnswerText(poll) {
      return this.formatAnswer(poll, this.getPollAnswer(poll.id));
    },
    isPollAnswerCorrect(poll) {
      return this.isAnswerCorrect(poll, this.getPollAnswer(poll.id));
    },
    formatAnswer(poll, answer) {
      if (poll.type === 'open') return String(answer || '');
      const selectedIds = Array.isArray(answer) ? answer : [answer];
      const selectedTexts = (poll.options || [])
        .filter((option) => selectedIds.map(String).includes(String(option.id)))
        .map((option) => option.text);
      return selectedTexts.length ? selectedTexts.join(', ') : 'Opción no disponible';
    },
    isAnswerCorrect(poll, answer) {
      if (poll.type === 'multiple') {
        const selectedIds = (Array.isArray(answer) ? answer : []).map(String).sort();
        const correctIds = (poll.correct_option_ids || []).map(String).sort();
        return selectedIds.length === correctIds.length
          && selectedIds.every((id, index) => id === correctIds[index]);
      }

      if (poll.type === 'open') {
        return null;
      }

      return String(answer) === String(poll.correct_option_id);
    },
    formatTime(seconds) {
      const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;

      return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.polls-panel {
  width: 100%;
  background: #fff;
  border: 1px solid #eef2f6;
  border-radius: 8px;
  padding: 20px;
}

.polls-panel--compact {
  border: 0;
  border-radius: 8px;
}

.polls-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.polls-panel__eyebrow {
  margin: 0 0 2px;
  color: #8a98a5;
  font: normal normal 400 12px/18px Poppins;
}

.polls-panel__title {
  margin: 0;
  color: #101820;
  font: normal normal 700 18px/26px Poppins;
}

.polls-panel__chip {
  background: #f6f8fa !important;
  color: #607080;
}

.polls-panel__active {
  display: grid;
  gap: 14px;
}

.polls-panel__time {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 999px;
  background: #e9f7fb;
  color: #0076ff;
  font: normal normal 700 12px/18px Poppins;
}

.polls-panel__time .v-icon {
  color: currentColor;
}

.polls-panel__active h4 {
  margin: 0;
  color: #101820;
  font: normal normal 700 16px/24px Poppins;
}

.polls-panel__options {
  display: grid;
  gap: 10px;
}

.polls-panel__option {
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #edf1f5;
  border-radius: 8px;
  background: #f8fafc;
  color: #26323d;
  cursor: pointer;
  text-align: left;
  font: normal normal 600 13px/20px Poppins;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.polls-panel__option:hover,
.polls-panel__option--selected {
  background: #f0f7ff;
  border-color: #0076ff;
  color: #0076ff;
}

.polls-panel__option:disabled {
  cursor: default;
  opacity: 1;
}

.polls-panel__option--correct {
  background: #edf9f1;
  border-color: #23a55a;
  color: #187a40;
}

.polls-panel__option--incorrect {
  background: #fff1f1;
  border-color: #dc3545;
  color: #b42332;
}

.polls-panel__option .v-icon {
  color: currentColor;
}

.polls-panel__actions {
  display: flex;
  justify-content: flex-end;
}

.polls-panel__result {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
}

.polls-panel__result > div {
  display: grid;
  gap: 2px;
}

.polls-panel__result strong,
.polls-panel__result span {
  font: normal normal 600 13px/20px Poppins;
}

.polls-panel__result--correct {
  background: #edf9f1;
  color: #187a40;
}

.polls-panel__result--incorrect {
  background: #fff1f1;
  color: #b42332;
}

.polls-panel__result--neutral {
  background: #f1f5f8;
  color: #526475;
}

.polls-panel__result .v-icon {
  color: currentColor;
}

.polls-panel__actions .v-btn {
  text-transform: none;
  font: normal normal 600 13px/20px Poppins;
}

.polls-panel__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 84px;
  padding: 18px;
  color: #6f7d8a;
  background: #f8fafc;
  border-radius: 8px;
  font: normal normal 400 13px/20px Poppins;
  text-align: center;
}

.polls-panel__list {
  display: grid;
  gap: 10px;
}

.polls-panel__item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #edf1f5;
  border-radius: 8px;
}

.polls-panel__item-main {
  display: grid;
  gap: 6px;
}

.polls-panel__timestamp {
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e9f7fb;
  color: #0076ff;
  font: normal normal 700 12px/18px Poppins;
}

.polls-panel__item p {
  margin: 0;
  color: #26323d;
  font: normal normal 400 13px/20px Poppins;
  word-break: break-word;
}

.polls-panel__saved-answer {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font: normal normal 600 12px/18px Poppins;
}

.polls-panel__saved-answer--correct {
  color: #187a40;
}

.polls-panel__saved-answer--incorrect {
  color: #b42332;
}

.polls-panel__saved-answer--neutral {
  color: #526475;
}

.polls-panel__saved-answer .v-icon {
  color: currentColor;
}

.polls-panel__status {
  flex: 0 0 auto;
  background: #fff6df !important;
  color: #9b6a00 !important;
}

.polls-panel__status--answered {
  background: #e9f7fb !important;
  color: #0076ff !important;
}

.polls-panel__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.polls-panel__pagination-meta {
  color: #607080;
  font: normal normal 400 12px/18px Poppins;
}

@media (max-width: 640px) {
  .polls-panel,
  .polls-panel--compact {
    padding: 18px 16px 16px;
    border: 0;
    border-radius: 18px 18px 0 0;
    box-shadow: 0 -14px 34px rgba(16, 24, 32, 0.18);
  }

  .polls-panel__eyebrow {
    display: none;
  }

  .polls-panel__title {
    font-size: 17px;
    line-height: 24px;
  }

  .polls-panel__active h4 {
    font-size: 15px;
    line-height: 23px;
  }

  .polls-panel__option {
    min-height: 52px;
    font-size: 13px;
  }

  .polls-panel__actions {
    justify-content: stretch;
  }

  .polls-panel__actions .v-btn {
    width: 100%;
    min-height: 42px;
  }

  .polls-panel__item {
    display: grid;
  }

  .polls-panel__status {
    width: fit-content;
  }

  .polls-panel__pagination {
    justify-content: center;
  }

  .polls-panel__pagination-meta {
    width: 100%;
    text-align: center;
  }
}
</style>
